import './playerView.css';

import { useState, useEffect, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { selectedPlayerState } from '../../recoil/atoms/selectedPlayerState';
import { playersState } from '../../recoil/atoms/playersState';
import { teamsState } from '../../recoil/atoms/teamsState';
import { competitionsState } from '../../recoil/atoms/competitionsState';
import { playerTeamDataSelector } from '../../recoil/selectors/playerTeamDataSelector';
import { playerCareersState } from '../../recoil/atoms/playerCareersState';
import { useSetSelectedPlayer } from '../../recoil/hooks/useSetSelectedPlayer';
import { useGetAndAddPlayerOverview } from '../../recoil/hooks/useGetAndAddPlayerOverview';
import { useGetAndAddPlayerCareer } from '../../recoil/hooks/useGetAndAddPlayerCareer';
import { useOpenGlobalModal } from '../../recoil/hooks/useOpenGlobalModal';
import { useAuthContext } from '../../../common/contexts/useAuthContext';
import { trackEvent } from '../../services/server/analytics/trackEvent';

import Dialog from '@mui/material/Dialog';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import CloudIcon from '@mui/icons-material/Cloud';
import TimelineIcon from '@mui/icons-material/Timeline';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import SummarizeIcon from '@mui/icons-material/Summarize';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import SsidChartIcon from '@mui/icons-material/SsidChart';
import EditIcon from '@mui/icons-material/Edit';
import WindowIcon from '@mui/icons-material/Window';
import RestoreIcon from '@mui/icons-material/Restore';
import AddIcon from '@mui/icons-material/Add';
// import GraphicEqIcon from '@mui/icons-material/GraphicEq';
// import GraphicEqIcon from '@mui/icons-material/GraphicEq';
// import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
// import TrackChangesIcon from '@mui/icons-material/TrackChanges';
// import LineAxisIcon from '@mui/icons-material/LineAxis';

import stringSimilarity from 'string-similarity';

import { translate } from '../../../common/language/translations';
import { getTenantColor } from '../../static/tenantConfigs';
import { PlayerViewCompare } from './PlayerViewCompare';
import { PlayerViewCareer } from './PlayerViewCareer';
import { PlayerViewReports } from './PlayerViewReports';
import { getReportsForPlayer } from '../../services/firestore/reports';
import { PlayerHistoryModal } from '../modals/historyModals/PlayerHistoryModal';
import { getCompletePlayerHistory } from '../../services/firestore/activities';
import { EditPlayerInfoModal } from '../modals/EditPlayerInfoModal';
import { getRoleIcon, getTeamIcon } from '../../utils/iconUtils';
import { IconButton } from '../controls/buttons/IconButton';
import { FixedTabs } from '../controls/navigation/FixedTabs';
import { PlayerImage } from '../display/images/PlayerImage';
import { CountryFlag } from '../display/images/CountryFlag';
import { ClubLogo } from '../display/images/ClubLogo';
import { modalPaperProps } from '../../static/constants/modals';
import { dateStringToTextFormat } from '../../utils/dateUtils';
import { TextButton } from '../controls/buttons/TextButton';
import { restorePlayerWithoutData } from '../../services/firestore/players';
import { PlayerViewStatistics } from './PlayerViewStatistics';
import { PlayerViewOverview } from './PlayerViewOverview';
import { getFirstAndLastSeason, getSeasonStartDate } from '../../utils/seasonUtils';
import { deepCloneObject } from '../../utils/commonUtils';
import { Club } from '../../types/club';
import { PlayerClubIteration, PlayerClubIterationBasicStats } from '../../types/player';
import { PositionInfo } from '../../types/position';
import { Activity } from '../../types/activity';
import { PlayerReport } from '../../types/report';
import { StringToElementMap, StringToNumberMap, StringToStringToNumberMap } from '../../types/common';
import { getPlayerPropertyValue } from '../../utils/playerUtils';
import { getAge } from '../../utils/ageUtils';
import { getDisplayPositions } from '../../utils/positionUtils';
import { getDisplayTeamName, getCurrentCompetitionName } from '../../utils/nameUtils';
import { getFlattenedReportVersionScoreKeys } from '../documents/report/reportVersions';


export interface ClubHistoryEntry {
  club: Club;

  position_info: PositionInfo;
  shouldComputeNewPrimaryAndSecondaryPositions?: boolean; // if data is aggregated from multiple seasons, we want to recompute position data from positions
  basic_stats: PlayerClubIterationBasicStats;

  iterations: PlayerClubIteration[];
  firstSeason?: string;
  lastSeason?: string;
  isCurrentPeriod?: boolean;
}


const clubsWithCustomDataTab = new Set([
  'demo',
  'aab',
  'viborg',
  'psv',
  // 'fokus',
]);


export const PlayerView: React.FC = () => {

  const { currentUser } = useAuthContext();

  const userConfig = useRecoilValue(userConfigState);
  const competitions = useRecoilValue(competitionsState);

  const selectedPlayer = useRecoilValue(selectedPlayerState);
  const { updateSelectedPlayer} = useSetSelectedPlayer();
  const { openPlayerViewModal, openConfirmModal } = useOpenGlobalModal();

  const players = useRecoilValue(playersState);
  const playerDocument = selectedPlayer && players ? players[selectedPlayer.id] : undefined;

  const playerCareers = useRecoilValue(playerCareersState);

  const teams = useRecoilValue(teamsState);
  const playerTeamData = useRecoilValue(playerTeamDataSelector);

  const getAndAddPlayerOverview = useGetAndAddPlayerOverview();
  const getAndAddClubIterationMap = useGetAndAddPlayerCareer();

  const [iterationsWithEventData, setIterationsWithEventData] = useState<PlayerClubIteration[] | undefined>(undefined);
  const [clubHistory, setClubHistory] = useState<ClubHistoryEntry[] | undefined>(undefined);
  const [nationalHistory, setNationalHistory] = useState<ClubHistoryEntry[] | undefined>(undefined);

  const [reports, setReports] = useState<PlayerReport[] | undefined>(undefined);
  const [playerHistory, setPlayerHistory] = useState<(Activity | PlayerReport)[]>([]);

  const [averageScoutingReportScores, setAverageScoutingReportScores] = useState<StringToStringToNumberMap>({});
  const [scoutingReportVersionCounts, setScoutingReportVersionCounts] = useState<StringToNumberMap>({});
  const [averageMatchReportScores, setAverageMatchReportScores] = useState<StringToStringToNumberMap | undefined>();

  const [averageScoutingReportScore, setAverageScoutingReportScore] = useState<number | undefined>();
  const [totalNumberOfScoutingReports, setTotalNumberOfScoutingReports] = useState<number | undefined>();

  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isEditPlayerInfoModalOpen, setIsEditPlayerInfoModalOpen] = useState(false);
  const [editPlayerInfoProperty, setEditPlayerInfoProperty] = useState<string | undefined>(undefined);

  const [playerViewIsExpanding, setPlayerViewIsExpanding] = useState(true);
  const [isConnectingPlayer, setIsConnectingPlayer] = useState(false);
  const [buttonHighlighted, setButtonHighlighted] = useState<string | undefined>(undefined);

  const isPlayerWithData = selectedPlayer !== undefined && !isNaN(Number(selectedPlayer.id));
  const isPlayerWithEventData = isPlayerWithData && selectedPlayer?.playerOverview?.event_data_exist;
  const isPlayerWithEventDataAvailable = isPlayerWithEventData && selectedPlayer?.playerOverview?.event_data_available;
  const isPlayerWithBasicStats = isPlayerWithData && selectedPlayer?.playerOverview?.basic_stats_exist;

  const isPlayerWithoutData = selectedPlayer !== undefined && isNaN(Number(selectedPlayer.id));
  const isExistingPlayerWithoutData: boolean = isPlayerWithoutData && selectedPlayer.id in players;
  const isDeletedPlayerWithoutData: boolean = isPlayerWithoutData && !(selectedPlayer.id in players) && selectedPlayer.id !== 'newPlayerWithoutData';
  const isNewPlayerWithoutData: boolean = isPlayerWithoutData && !(selectedPlayer.id in players) && selectedPlayer.id === 'newPlayerWithoutData';

  const tabOptions = [
    'overview',
    'statistics',
    'compare',
    'career',
  ];

  if (userConfig?.accessConfig.reportAccess !== 'none') {
    tabOptions.push('reports');
  }

  if (clubsWithCustomDataTab.has(userConfig?.club ?? '')) {
    tabOptions.push('customData');
  }

  const tabOptionToTabIcon: StringToElementMap = {
    'overview': <WindowIcon key={'auto-fix-high-icon'} style={{ fontSize: 21 }} />,
    'statistics': <EqualizerIcon key={'equalizer-icon'} style={{ fontSize: 21 }} />,
    'compare': <SsidChartIcon key={'ssid-icon'} style={{ fontSize: 21, marginTop: 0.5 }} />,
    'career': <TimelineIcon key={'timeline-icon'} style={{ fontSize: 21 }} />,
    'reports': <SummarizeIcon key={'summarize-icon'} style={{ fontSize: 21 }} />,
    'customData': <CloudIcon key={'library-books-icon'} style={{ fontSize: 21 }} />,
  };

  const tabOptionToStatus = {
    'overview': 'enabled',
    'statistics': isPlayerWithEventDataAvailable
      ? 'enabled'
      : isPlayerWithEventData
        ? 'locked'
        : 'blocked',
    'compare': isPlayerWithEventDataAvailable
      ? 'enabled'
      : isPlayerWithEventData
        ? 'locked'
        : 'blocked',
    'career': isPlayerWithBasicStats
      ? 'enabled'
      : 'blocked',
    'reports': 'enabled',
    'customData': 'enabled'
  };


  const activeSubTab = selectedPlayer?.activeSubTab ?? 0;
  const setActiveSubTab = (subTab: number) => {
    updateSelectedPlayer(selectedPlayer, { activeSubTab: subTab, reportHasUnsavedChanges: false });
  };


  const handleExitPlayerViewClick = () => {
    if (selectedPlayer?.reportHasUnsavedChanges) {
      openConfirmModal(
        () => updateSelectedPlayer(undefined),
        translate('exitReport?', userConfig?.language),
        translate('exitReportInfo', userConfig?.language),
      );
    }
    else {
      updateSelectedPlayer(undefined);
    }
  };


  const handleSubTabClick = (subTab: number) => {
    if (selectedPlayer?.reportHasUnsavedChanges) {
      openConfirmModal(
        () => setActiveSubTab(subTab),
        translate('exitReport?', userConfig?.language),
        translate('exitReportInfo', userConfig?.language),
      );
    }
    else {
      setActiveSubTab(subTab);
    }
  };


  useEffect(() => {
    if (selectedPlayer?.id && !selectedPlayer?.playerOverview && !isNaN(Number(selectedPlayer.id))) {
      getAndAddPlayerOverview(selectedPlayer.id, currentUser).then(newPlayerOverview => {
        updateSelectedPlayer(selectedPlayer, { playerOverview: newPlayerOverview });
      });
    }
  }, [currentUser, selectedPlayer?.id]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (selectedPlayer?.id && userConfig) {
      getAndAddClubIterationMap(selectedPlayer.id, currentUser);

      getReportsForPlayer(selectedPlayer.id, userConfig, currentUser).then(reports => {
        setReports(reports);
      });
    }
  }, [currentUser, selectedPlayer?.id, userConfig?.club]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (teams && userConfig?.club && selectedPlayer?.id && reports) {
      getCompletePlayerHistory(selectedPlayer.id, reports, userConfig).then(history => {
        setPlayerHistory(history);
      });
    }
  }, [teams, reports, userConfig, selectedPlayer?.id]);


  const onClosePlayerModalFeedback = () => {
    setButtonHighlighted('statusButton');
    setTimeout(() => {
      setButtonHighlighted(undefined);
    }, 800);
  };


  const handleStatusButtonClick = () => {
    if (selectedPlayer && buttonHighlighted !== 'statusButton') {
      openPlayerViewModal(
        selectedPlayer.id,
        selectedPlayer.name,
        onClosePlayerModalFeedback,
      );
    }
  };


  const handleEditInfoIconClick = (infoProperty: string) => {
    if (buttonHighlighted !== infoProperty) {
      setIsEditPlayerInfoModalOpen(true);
      setEditPlayerInfoProperty(infoProperty);
    }
  };


  const closeEditPlayerInfoModal = (
    editPlayerInfoProperty?: string | undefined,
    newPlayerName?: string | undefined
  ) => {
    setIsEditPlayerInfoModalOpen(false);

    if (editPlayerInfoProperty === 'role' || editPlayerInfoProperty === 'academyRole') {
      setTimeout(() => {
        setButtonHighlighted(editPlayerInfoProperty);
      }, 100);
      setTimeout(() => {
        setButtonHighlighted(undefined);
      }, 700);
    }
    else {
      setButtonHighlighted(editPlayerInfoProperty);
      setTimeout(() => {
        setButtonHighlighted(undefined);
      }, 800);
    }

    setTimeout(() => {
      setEditPlayerInfoProperty(undefined);
    }, 200);

    if (newPlayerName) {
      updateSelectedPlayer(selectedPlayer, { name: newPlayerName });
    }
  };


  const shouldShowFullname = (name: string, fullname: string) => {
    const normalizedName = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const normalizedFullname = fullname.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return stringSimilarity.compareTwoStrings(normalizedFullname, normalizedName) < 0.95;
  };


  const getDisplayName = () => {
    const name = selectedPlayer?.playerOverview?.name ?? selectedPlayer?.name ?? '-';
    const fullname = selectedPlayer?.playerOverview?.fullname;

    // if fullname is different from name, we show both
    if (fullname && shouldShowFullname(name, fullname)) {
      return (
        <div className='flex-row align-center'>
          {name}

          <div className='player-view-additional-info-value' style={{ marginLeft: 12, marginBottom: -1 }}>
            {fullname}
          </div>
        </div>
      );
    }

    return name;
  };


  const getPlayerRoleIcon = (roleProperty: 'role' | 'academyRole') => {
    if (selectedPlayer?.id) {
      return getRoleIcon(players[selectedPlayer.id]?.[roleProperty], 20, buttonHighlighted === roleProperty ? tenantColor : undefined, undefined, true);
    }
  };


  // for goalkeepers, we want to treat 'K' and 'GK' as the same position, to avoid showing 'Goalkeeper (GK)' etc.
  const getCareerPositionInfo = (positionInfo: PositionInfo) => {
    const newPositionInfo: PositionInfo = {
      positions: [],
      primary_positions: [],
      secondary_positions: [],
    };

    positionInfo.positions.forEach(position => {
      newPositionInfo.positions.push({ ...position, position: position.position === 'G' ? 'GK' : position.position });
    });

    positionInfo.primary_positions.forEach(position => {
      newPositionInfo.primary_positions.push(position === 'G' ? 'GK' : position);
    });

    positionInfo.secondary_positions.forEach(position => {
      newPositionInfo.secondary_positions.push(position === 'G' ? 'GK' : position);
    });

    return newPositionInfo;
  };


  useEffect(() => {
    if (selectedPlayer?.playerOverview && playerCareers && selectedPlayer?.id && selectedPlayer.id in playerCareers) {

      const playerCareer = playerCareers[Number(selectedPlayer.id)];

      const clubIterationArray: PlayerClubIteration[] = [];
      const nationalIterationArray: PlayerClubIteration[] = [];
      const iterationsWithEventDataArray: PlayerClubIteration[] = [];

      // (1) group iterations into categories
      Object.keys(playerCareer).forEach(clubIterationId => {
        const clubIteration = playerCareer[clubIterationId];

        if (clubIteration.event_data_exist) {
          iterationsWithEventDataArray.push({ ...clubIteration, 'club_iteration_id': clubIterationId });
        }

        if (clubIteration.club?.is_national_team) {
          nationalIterationArray.push(clubIteration);
        }
        else {
          clubIterationArray.push(clubIteration);
        }
      });

      clubIterationArray.sort(
        (a, b) => {
          const aPlotData = a.basic_stats?.plot_date ?? getSeasonStartDate(a.season).toISOString();
          const bPlotData = b.basic_stats?.plot_date ?? getSeasonStartDate(b.season).toISOString();
          return bPlotData.localeCompare(aPlotData);
        }
      );

      iterationsWithEventDataArray.sort(
        (a, b) => {
          const aPlotData = a.basic_stats?.plot_date ?? getSeasonStartDate(a.season).toISOString();
          const bPlotData = b.basic_stats?.plot_date ?? getSeasonStartDate(b.season).toISOString();
          return bPlotData.localeCompare(aPlotData);
        }
      );

      // (2) aggregate consecutive seasons for the same club into one row
      const clubHistoryArray: ClubHistoryEntry[] = [];
      let lastClubEntry = null as ClubHistoryEntry | null;

      clubIterationArray.forEach((iteration, index) => {

        // we either merge into the last entry, or we push the last entry and start a new one
        if (lastClubEntry && lastClubEntry.club.id === iteration.club.id) {
          lastClubEntry.basic_stats.appearances += iteration.basic_stats.appearances;
          lastClubEntry.basic_stats.games_started += iteration.basic_stats.games_started;
          lastClubEntry.basic_stats.n_goals += iteration.basic_stats.n_goals;
          lastClubEntry.basic_stats.n_assists += iteration.basic_stats.n_assists;

          const careerPositionInfo = getCareerPositionInfo(iteration.position_info);
          lastClubEntry.iterations.push(iteration);
          lastClubEntry.position_info.positions.push(...careerPositionInfo.positions);
          lastClubEntry.shouldComputeNewPrimaryAndSecondaryPositions = true;
          lastClubEntry.position_info.primary_positions = [];
          lastClubEntry.position_info.secondary_positions = [];
        }
        else {
          if (lastClubEntry) {
            const firstAndLastSeason = getFirstAndLastSeason(lastClubEntry.iterations.map(iteration => iteration.season));
            lastClubEntry.firstSeason = firstAndLastSeason.firstSeason;
            lastClubEntry.lastSeason = firstAndLastSeason.lastSeason;
            clubHistoryArray.push(lastClubEntry);
          }

          const careerPositionInfo = getCareerPositionInfo(iteration.position_info);
          lastClubEntry = {
            club: iteration.club,
            position_info: careerPositionInfo,
            basic_stats: deepCloneObject(iteration.basic_stats),
            iterations: [iteration],
            firstSeason: iteration.season,
            isCurrentPeriod: index === 0 && selectedPlayer?.playerOverview?.club && iteration.club.id === selectedPlayer?.playerOverview?.club.id,
          };
        }
      });

      if (lastClubEntry) {
        const firstAndLastSeason = getFirstAndLastSeason(lastClubEntry.iterations.map(iteration => iteration.season));
        lastClubEntry.firstSeason = firstAndLastSeason.firstSeason;
        lastClubEntry.lastSeason = firstAndLastSeason.lastSeason;
        clubHistoryArray.push(lastClubEntry);
      }


      // (3) aggregate all national season with the same country_code and club_name into one row
      const nationalHistoryArray: ClubHistoryEntry[] = [];
      nationalIterationArray.forEach(iteration => {
        const existingEntry = nationalHistoryArray.find(entry => entry.club.country_code === iteration.club.country_code && entry.club.name === iteration.club.name);
        if (existingEntry) {
          existingEntry.basic_stats.appearances += iteration.basic_stats.appearances;
          existingEntry.basic_stats.games_started += iteration.basic_stats.games_started;
          existingEntry.basic_stats.n_goals += iteration.basic_stats.n_goals;
          existingEntry.basic_stats.n_assists += iteration.basic_stats.n_assists;

          existingEntry.iterations.push(iteration);
          existingEntry.position_info.positions.push(...iteration.position_info.positions);
          existingEntry.shouldComputeNewPrimaryAndSecondaryPositions = true;
          existingEntry.position_info.primary_positions = [];
          existingEntry.position_info.secondary_positions = [];
        }
        else {
          nationalHistoryArray.push({
            club: iteration.club,
            position_info: getCareerPositionInfo(iteration.position_info),
            basic_stats: deepCloneObject(iteration.basic_stats),
            iterations: [iteration],
          });
        }
      });

      nationalHistoryArray.sort(
        (a, b) => {
          if (a.club.country_code && b.club.country_code && a.club.country_code !== b.club.country_code) {
            return a.club.country_code.localeCompare(b.club.country_code);
          }
          return !a.club?.name
            ? -1
            : !b.club.name
              ? 1
              : b.club.name.localeCompare(a.club.name);
        }
      );

      setClubHistory(clubHistoryArray);
      setNationalHistory(nationalHistoryArray);
      setIterationsWithEventData(iterationsWithEventDataArray);
    }
  }, [playerCareers, selectedPlayer?.playerOverview, selectedPlayer?.id]);


  useEffect(() => {
    if (userConfig?.club && reports) {

      const scoutingReportScoreSums: StringToStringToNumberMap = {}; // version to score to score sum
      const matchReportScoreSums: StringToStringToNumberMap = {}; // version to score to score sum

      const scoutingReportScoreCounts: StringToStringToNumberMap = {}; // version to score to score count
      const matchReportScoreCounts: StringToStringToNumberMap = {}; // version to score to score count

      const scoutingReportVersionCounts: StringToNumberMap = {}; // version to count

      let totalOverallScoutingReportScore = 0;
      let totalScoutingReportCount= 0;

      // aggregate report scores by version
      reports.forEach(report => {

        // we do not consider deleted reports
        if (!report.isDeleted) {

          const scoreKeys = getFlattenedReportVersionScoreKeys(report.reportVersion);

          if (report.reportType === 'ScoutingReport') {
            totalOverallScoutingReportScore += report.reportFields['overallScore'];
            totalScoutingReportCount++;

            if (!scoutingReportVersionCounts[report.reportVersion]) scoutingReportVersionCounts[report.reportVersion] = 0;
            scoutingReportVersionCounts[report.reportVersion]++;

            if (!scoutingReportScoreSums[report.reportVersion]) scoutingReportScoreSums[report.reportVersion] = {};
            if (!scoutingReportScoreCounts[report.reportVersion]) scoutingReportScoreCounts[report.reportVersion] = {};

            scoreKeys.forEach(scoreKey => {
              if (!scoutingReportScoreSums[report.reportVersion][scoreKey]) {
                scoutingReportScoreSums[report.reportVersion][scoreKey] = 0;
              }

              const score = report.reportFields[scoreKey];
              if (score) {
                scoutingReportScoreSums[report.reportVersion][scoreKey] += score;

                if (!scoutingReportScoreCounts[report.reportVersion][scoreKey]) {
                  scoutingReportScoreCounts[report.reportVersion][scoreKey] = 0;
                }
                scoutingReportScoreCounts[report.reportVersion][scoreKey]++;
              }
            });
          }

          else if (report.reportType === 'MatchReport') {

            if (!matchReportScoreSums[report.reportVersion]) matchReportScoreSums[report.reportVersion] = {};
            if (!matchReportScoreCounts[report.reportVersion]) matchReportScoreCounts[report.reportVersion] = {};

            scoreKeys.forEach(scoreKey => {
              if (!matchReportScoreSums[report.reportVersion][scoreKey]) {
                matchReportScoreSums[report.reportVersion][scoreKey] = 0;
              }

              const score = report.reportFields[scoreKey];
              if (score) {
                matchReportScoreSums[report.reportVersion][scoreKey] += score;

                if (!matchReportScoreCounts[report.reportVersion][scoreKey]) {
                  matchReportScoreCounts[report.reportVersion][scoreKey] = 0;
                }
                matchReportScoreCounts[report.reportVersion][scoreKey]++;
              }
            });
          }
        }
      });

      const averageScoutingReportScoresToSet: StringToStringToNumberMap = {};
      const averageMatchReportScoresToSet: StringToStringToNumberMap = {};

      Object.keys(scoutingReportScoreSums).forEach(reportVersion => {
        const scoreKeys = getFlattenedReportVersionScoreKeys(reportVersion);

        scoreKeys.forEach(scoreKey => {
          if (!averageScoutingReportScoresToSet[reportVersion]) averageScoutingReportScoresToSet[reportVersion] = {};
          if (scoutingReportScoreCounts[reportVersion][scoreKey]) {
            averageScoutingReportScoresToSet[reportVersion][scoreKey] =
              scoutingReportScoreSums[reportVersion][scoreKey] / scoutingReportScoreCounts[reportVersion][scoreKey];
          }
        });
      });

      Object.keys(matchReportScoreSums).forEach(reportVersion => {
        const scoreKeys = getFlattenedReportVersionScoreKeys(reportVersion);

        scoreKeys.forEach(scoreKey => {
          if (!averageMatchReportScoresToSet[reportVersion]) averageMatchReportScoresToSet[reportVersion] = {};
          if (matchReportScoreCounts[reportVersion][scoreKey]) {
            averageMatchReportScoresToSet[reportVersion][scoreKey] = matchReportScoreSums[reportVersion][scoreKey] / matchReportScoreCounts[reportVersion][scoreKey];
          }
        });
      });

      setAverageScoutingReportScores(averageScoutingReportScoresToSet);
      setScoutingReportVersionCounts(scoutingReportVersionCounts);
      setAverageMatchReportScores(averageMatchReportScoresToSet);

      setAverageScoutingReportScore(totalOverallScoutingReportScore / totalScoutingReportCount);
      setTotalNumberOfScoutingReports(totalScoutingReportCount);
    }
  }, [userConfig?.club, reports]);


  useEffect(() => {
    const timer = setTimeout(() => {
      setPlayerViewIsExpanding(false);
    }, 150);

    return () => {
      clearTimeout(timer);
    };
  }, []);


  const tenantColor = getTenantColor(userConfig?.club ?? '');

  const sortedTeamIds = useMemo(() => {
    if (!selectedPlayer?.id || !playerTeamData || !teams) return [];
    const currentTeamIds = selectedPlayer.id in playerTeamData ? Object.keys(playerTeamData[selectedPlayer.id]) : [];
    return currentTeamIds.sort((a, b) => {
      const teamA = teams[a];
      const teamB = teams[b];
      if (teamA.isAcademyTeam === teamB.isAcademyTeam) {
        return teamA.orderIndex - teamB.orderIndex;
      }
      return teamA.isAcademyTeam ? 1 : -1;
    });
  }, [selectedPlayer?.id, playerTeamData, teams]);

  const playerIsInOwnTeam = playerTeamData && selectedPlayer?.id && selectedPlayer.id in playerTeamData && playerTeamData[selectedPlayer.id]['ownTeam'];
  const playerIsInAcademy = players && selectedPlayer?.id && selectedPlayer.id in players && players[selectedPlayer.id].isAcademyPlayer;


  const birthDate = getPlayerPropertyValue('birth_date', playerDocument, selectedPlayer?.playerOverview);


  return (
    <div className='player-view-section'>

      <Dialog
        open={isEditPlayerInfoModalOpen}
        onClose={() => closeEditPlayerInfoModal()}
        slotProps={{ paper: modalPaperProps }}
      >
        <EditPlayerInfoModal
          editPlayerInfoProperty={editPlayerInfoProperty}
          closeModal={closeEditPlayerInfoModal}
          playerToEdit={{
            ...selectedPlayer,
            contract_expiration: getPlayerPropertyValue('contract_expiration', playerDocument, selectedPlayer?.playerOverview),
            contract_expiration_in_database: selectedPlayer?.playerOverview?.contract_expiration,
            birth_date: getPlayerPropertyValue('birth_date', playerDocument, selectedPlayer?.playerOverview),
            birth_date_in_database: selectedPlayer?.playerOverview?.birth_date,
            role: getPlayerPropertyValue('role', playerDocument),
            academyRole: getPlayerPropertyValue('academyRole', playerDocument),
            position: getPlayerPropertyValue('position', playerDocument),
            clubName: getPlayerPropertyValue('clubName', playerDocument),
          }}
        />
      </Dialog>

      <Dialog
        open={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        slotProps={{ paper: modalPaperProps }}
      >
        <PlayerHistoryModal
          history={playerHistory}
          closeModal={() => setIsHistoryModalOpen(false)}
        />
      </Dialog>


      <div className='player-view-header'>

        <IconButton
          icon={<ArrowBackIcon style={{ fontSize: 26 }} />}
          onClick={() => handleExitPlayerViewClick()}
          size={32}
          style={{ position: 'absolute', top: 11, left: 11, zIndex: 5 }}
          startWhite={true}
        />

        <PlayerImage
          src={selectedPlayer?.playerOverview?.image_url}
          isSingleImage={true}
          containerStyle={{ margin: 'auto', width: '13vh', height: 'auto', minWidth: 80, marginLeft: 'calc(2% + 22px)' }}
          imageStyle={{ width: '80%', height: 'auto' }}
        />

        <div className='player-view-info-section'>

          <div className='player-view-name'>
            {getDisplayName()}

            {isExistingPlayerWithoutData && (
              <IconButton
                onClick={() => handleEditInfoIconClick('name')}
                icon={<EditIcon style={{ fontSize: 16 }} />}
                size={24}
                style={{ marginLeft: 12, zIndex: 3 }}
                startWhite={true}
                hoverText={translate('editName', userConfig?.language)}
              />
            )}

            {playerIsInOwnTeam && userConfig?.accessConfig.hasAccessToViewTeamRoles &&
              (
                <IconButton
                  onClick={() => handleEditInfoIconClick('role')}
                  icon={getPlayerRoleIcon('role')}
                  size={29}
                  style={{ marginLeft: 12, zIndex: 2 }}
                  startWhite={true}
                  hoverText={translate(playerDocument?.role ? 'editTeamRole' : 'addTeamRole', userConfig?.language)}
                  disabled={!userConfig?.accessConfig.hasAccessToModifyOwnTeam}
                />
              )
            }

            {playerIsInAcademy && userConfig?.accessConfig.hasAccessToAcademy &&
              (
                <IconButton
                  onClick={() => handleEditInfoIconClick('academyRole')}
                  icon={getPlayerRoleIcon('academyRole')}
                  size={29}
                  style={{ marginLeft: 12, zIndex: 1 }}
                  startWhite={true}
                  hoverText={translate(playerDocument?.academyRole ? 'editAcademyRole' : 'addAcademyRole', userConfig?.language)}
                />
              )
            }
          </div>

          {userConfig && (
            <div className='player-view-info-row'>

              <div className='player-view-info-column player-view-info-age-column'>
                <div className='player-view-info-label'>
                  {translate('age', userConfig?.language)}
                </div>
                <div className='player-view-info-value flex-row align-center' style={{ paddingRight: isExistingPlayerWithoutData ? 20 : undefined }}>
                  {birthDate ? getAge(birthDate) : '-'}

                  {birthDate && (
                    <div className='player-view-additional-info-value' style={{ marginLeft: 7, marginBottom: -2 }}>
                      {dateStringToTextFormat(birthDate)}
                    </div>
                  )}

                  {isExistingPlayerWithoutData && (
                    <IconButton
                      onClick={() => handleEditInfoIconClick('birth_date')}
                      icon={<EditIcon style={{ fontSize: 16 }} />}
                      size={24}
                      style={{ position: 'absolute', top: -2, right: -8 }}
                      iconColor={buttonHighlighted === 'birth_date' ? tenantColor : undefined}
                    />
                  )}
                </div>
              </div>

              <div className='player-view-info-column'>
                <div className='player-view-info-label'>
                  {translate('position', userConfig?.language)}
                </div>

                {isPlayerWithData && (
                  <div className='player-view-info-value' style={{ paddingRight: isExistingPlayerWithoutData ? 20 : undefined }}>
                    {getDisplayPositions(selectedPlayer?.playerOverview?.position_info.primary_positions, userConfig.language) ?? '-'}

                    {isExistingPlayerWithoutData && (
                      <IconButton
                        onClick={() => handleEditInfoIconClick('position')}
                        icon={<EditIcon style={{ fontSize: 16 }} />}
                        size={24}
                        style={{ position: 'absolute', top: -2, right: -8 }}
                        iconColor={buttonHighlighted === 'position' ? tenantColor : undefined}
                      />
                    )}
                  </div>
                )}

                {isPlayerWithoutData && (
                  <div className='player-view-info-value' style={{ paddingRight: isExistingPlayerWithoutData ? 20 : undefined }}>
                    {playerDocument?.position ? translate(playerDocument.position, userConfig.language) : '-'}

                    {isExistingPlayerWithoutData && (
                      <IconButton
                        onClick={() => handleEditInfoIconClick('position')}
                        icon={<EditIcon style={{ fontSize: 16 }} />}
                        size={24}
                        style={{ position: 'absolute', top: -2, right: -8 }}
                        iconColor={buttonHighlighted === 'position' ? tenantColor : undefined}
                      />
                    )}
                  </div>
                )}

              </div>

              <div className='player-view-info-column'>
                <div className='player-view-info-label'>
                  {translate('nationality', userConfig?.language)}
                </div>
                <div className='player-view-info-value' style={{ paddingRight: isExistingPlayerWithoutData ? 20 : undefined }}>
                  <CountryFlag
                    countryCode={getPlayerPropertyValue('country_code', playerDocument, selectedPlayer?.playerOverview)}
                    width={16}
                    showCountryName={true}
                    showHyphenIfEmpty={true}
                    containerStyle={{ justifyContent: 'left', gap: 5 }}
                    imageStyle={{ marginBottom: -1 }}
                  />

                  {isExistingPlayerWithoutData && (
                    <IconButton
                      onClick={() => handleEditInfoIconClick('country_code')}
                      icon={<EditIcon style={{ fontSize: 16 }} />}
                      size={24}
                      style={{ position: 'absolute', top: -2, right: -8 }}
                      iconColor={buttonHighlighted === 'country_code' ? tenantColor : undefined}
                    />
                  )}
                </div>
              </div>

              {isPlayerWithData && (
                <div className='player-view-info-column'>
                  <div className='player-view-info-label'>
                    {translate('league', userConfig?.language)}
                  </div>
                  <div className='player-view-info-value'>{getCurrentCompetitionName(selectedPlayer?.playerOverview?.club, competitions)}</div>
                </div>
              )}

              <div className='player-view-info-column'>
                <div className='player-view-info-label' style={{ display: 'flex', flexDirection: 'row' }}>
                  {translate('club', userConfig?.language)}

                  {selectedPlayer?.playerOverview?.on_loan_from && (
                    <div className='player-view-on-loan-info'>
                      <div style={{ marginTop: -1 }}>
                        {'('}
                      </div>

                      {translate('loanFrom', userConfig.language, true)}

                      <ClubLogo
                        club={selectedPlayer?.playerOverview?.on_loan_from}
                        size={12}
                        showClubName={true}
                        containerStyle={{ gap: 2, marginLeft: 3 }}
                      />

                      <div style={{ marginTop: -1 }}>
                        {')'}
                      </div>
                    </div>
                  )}
                </div>

                {isPlayerWithData && (
                  <div className='player-view-info-value'>
                    <ClubLogo
                      club={selectedPlayer?.playerOverview?.club}
                      size={18}
                      showClubName={true}
                      containerStyle={{ gap: 3 }}
                      showHyphenIfEmpty={true}
                    />
                  </div>
                )}

                {isPlayerWithoutData && (
                  <div className='player-view-info-value' style={{ paddingRight: isExistingPlayerWithoutData ? 20 : undefined }}>
                    {playerDocument?.clubName ?? '-'}

                    {isExistingPlayerWithoutData && (
                      <IconButton
                        onClick={() => handleEditInfoIconClick('clubName')}
                        icon={<EditIcon style={{ fontSize: 16 }} />}
                        size={24}
                        style={{ position: 'absolute', top: -2, right: -8 }}
                        iconColor={buttonHighlighted === 'clubName' ? tenantColor : undefined}
                      />
                    )}
                  </div>
                )}
              </div>

              {isPlayerWithData && (
                <div className='player-view-info-column'>
                  <div className='player-view-info-label'>
                    {translate('height', userConfig?.language)}
                  </div>
                  <div className='player-view-info-value'>
                    {getPlayerPropertyValue('height', playerDocument, selectedPlayer?.playerOverview) ?? '-'}
                  </div>
                </div>
              )}

              <div className='player-view-info-column player-view-info-contract-column'>
                <div className='player-view-info-label'>
                  {translate('contract', userConfig?.language)}
                </div>
                <div className='player-view-info-value'>
                  {getPlayerPropertyValue(
                    'contract_expiration',
                    playerDocument,
                    selectedPlayer?.playerOverview,
                    undefined,
                    (value) => dateStringToTextFormat(value),
                    '-'
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {(
          (playerTeamData && selectedPlayer && playerTeamData[selectedPlayer.id]?.['ownTeam'] && userConfig?.accessConfig.hasAccessToModifyOwnTeam)
          || (players && selectedPlayer && players[selectedPlayer.id]?.isAcademyPlayer && userConfig?.accessConfig.hasAccessToAcademy)
        ) && (
          <IconButton
            onClick={() => handleEditInfoIconClick('contract_expiration')}
            icon={<EditCalendarIcon style={{ fontSize: 18 }} />}
            size={24}
            style={{ position: 'absolute', top: 'calc(6vh - 10px)', right: 'calc(23% + 10px)', zIndex: 1 }}
            startWhite={true}
            iconColor={buttonHighlighted === 'contract_expiration' ? tenantColor : undefined}
            hoverText={translate('editContract', userConfig?.language)}
            hoverStyle={{ fontSize: 11, height: 20, marginLeft: -8, marginTop: -20 }}
          />
        )}

        <IconButton
          onClick={() => { trackEvent('PlayerHistoryViewed', {}, currentUser, 'user'); setIsHistoryModalOpen(true); }}
          icon={<LibraryBooksIcon style={{ fontSize: 20 }} />}
          size={30}
          style={{ position: 'absolute', top: '6.5vh', right: '19.3%', zIndex: 1 }}
          startWhite={true}
          hoverText={translate('playerHistory', userConfig?.language)}
          hoverStyle={{ fontSize: 11, height: 20, marginLeft: -50, marginTop: -32 }}
        />

        {!isDeletedPlayerWithoutData && (
          <div className='player-view-team-status-section'>
            <div className='player-view-team-status-text'>
              {translate('teamStatus', userConfig?.language)}
            </div>

            <div
              className='player-view-team-status-button'
              style={{
                marginTop: sortedTeamIds.length > 2 ? 6 : sortedTeamIds.length > 1 ? 8 : 12,
                marginBottom: sortedTeamIds.length > 2 ? 0 : sortedTeamIds.length > 1 ? -3 : -8,
                backgroundColor: buttonHighlighted === 'statusButton' ? tenantColor : undefined
              }}
              onClick={() => handleStatusButtonClick()}
            >

              {sortedTeamIds.length === 0 && (
                <div className='player-view-team-status-button-row'>
                  <div style={{ marginLeft: 9 }}>
                    {translate('addToTeam', userConfig?.language)}
                  </div>
                  <div className='player-view-team-status-button-right-icon'>
                    <AddIcon style={{ fontSize: 18 }} />
                  </div>
                </div>
              )}

              {sortedTeamIds.map(teamId => (
                <div
                  key={teamId}
                  className='player-view-team-status-button-row'
                >
                  <div className='player-view-team-status-button-left-icon'>
                    {getTeamIcon(teamId, 18, undefined, teams?.[teamId]?.isAcademyTeam)}
                  </div>

                  {getDisplayTeamName(teamId, teams, userConfig?.language, undefined)}

                  {playerTeamData && selectedPlayer && selectedPlayer.id in playerTeamData && buttonHighlighted !== 'statusButton' && (
                    <div className='player-view-team-status-button-position'>
                      {translate(playerTeamData[selectedPlayer.id][teamId], userConfig?.language)}
                    </div>
                  )}

                  <div
                    className={
                      'player-view-team-status-button-row-overlay' +
                    (buttonHighlighted === 'statusButton' ? ' player-view-team-status-button-row-overlay-highlighted' : '')
                    }
                  />
                </div>
              ))}


            </div>
          </div>
        )}

        {isDeletedPlayerWithoutData && (
          <div className='player-view-player-status-text'>
            {translate('deletedPlayer', userConfig?.language)}
          </div>

        )}

      </div>

      {!isNewPlayerWithoutData && (
        <FixedTabs
          tabOptions={tabOptions}
          tabIcons={tabOptions.map(tabOption => tabOptionToTabIcon[tabOption])}
          activeTab={activeSubTab}
          setActiveTab={handleSubTabClick}
          parentTab={'playerView'}
          marginTop={'13vh'}
          marginTopLine={'16.9vh'}
          isPlayerView={true}
          playerViewTabOptionToStatus={tabOptionToStatus}
        />
      )}

      <div className='player-view-main-section'>
        {isNewPlayerWithoutData && (
          <div className='player-view-deleted-player-text'>
            {translate('playerDoesNotExistYet', userConfig?.language)}
          </div>
        )}

        {activeSubTab === 0 && isDeletedPlayerWithoutData && !isConnectingPlayer && (
          <div className='player-view-deleted-player-text'>
            {translate('playerIsDeleted', userConfig?.language)}

            {selectedPlayer && userConfig && (
              <TextButton
                onClick={() => restorePlayerWithoutData(selectedPlayer.id, selectedPlayer.name, userConfig?.club, currentUser)}
                text={translate('restorePlayer', userConfig?.language)}
                icon={<RestoreIcon style={{ fontSize: 20 }} />}
              />
            )}
          </div>
        )}

        {activeSubTab === 0 && !isDeletedPlayerWithoutData && !isNewPlayerWithoutData && (
          <PlayerViewOverview
            isExistingPlayerWithoutData={isExistingPlayerWithoutData}
            setIsConnectingPlayer={setIsConnectingPlayer}

            clubHistory={clubHistory}
            nationalHistory={nationalHistory}
            iterationsWithEventData={iterationsWithEventData}

            averageScoutingReportOverallScore={averageScoutingReportScore}
            numberOfScoutingReportsWithOverallScore={totalNumberOfScoutingReports}
          />
        )}

        {activeSubTab === 1 && isPlayerWithEventDataAvailable && !isConnectingPlayer && (
          <PlayerViewStatistics
            iterationsWithEventData={iterationsWithEventData}
          />
        )}

        {activeSubTab === 2 && isPlayerWithEventDataAvailable && !isConnectingPlayer && (
          <PlayerViewCompare
            iterationsWithEventData={iterationsWithEventData}
          />
        )}

        {activeSubTab === 3 && isPlayerWithBasicStats && !isConnectingPlayer && (
          <PlayerViewCareer
            clubHistory={clubHistory}
            nationalHistory={nationalHistory}
          />
        )}

        {activeSubTab === 4 && !isConnectingPlayer && (
          <PlayerViewReports
            reports={reports ?? []}
            setReports={setReports}
            averageScoutingReportScores={averageScoutingReportScores}
            scoutingReportVersionCounts={scoutingReportVersionCounts}
            averageMatchReportScores={averageMatchReportScores}
            playerViewIsExpanding={playerViewIsExpanding}
          />
        )}

        {activeSubTab === 5 && (
          <div className='full-size-container'>
            <div className='player-view-custom-data-section'>
              <div className='player-view-custom-data-section-headline'>
                {translate('customDataMessageHeadline', userConfig?.language)}
              </div>
              <div className='player-view-custom-data-section-icon'>
                <CloudIcon style={{ fontSize: 60 }} />
              </div>
              <div className='player-view-custom-data-section-text'>
                {translate('customDataMessageText', userConfig?.language)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
